import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalBody, ModalFooter, useToast } from '../sectepe-ui/index';
import { templateApi, Template } from '../services/api';
import { PageHeader, EmptyState, LoadingState, StatusBadge, useConfirm } from '../components/ui';
import { Icon } from '../components/icons';
import { useUrlParams } from '../vertrieb/useUrlParams';
import { fehlerText } from '../vertrieb/format';
import { kategorieLabel } from '../inhalte/templateKategorien';

type Art = 'alle' | 'system' | 'eigene';

const ARTEN: { id: Art; label: string }[] = [
  { id: 'alle', label: 'Alle' },
  { id: 'system', label: 'Vorlagen' },
  { id: 'eigene', label: 'Eigene' },
];

/**
 * Prompt-Templates: fertige Vorlagen und eigene. "Verwenden" oeffnet den
 * Text-Generator mit dem Template und seinen Variablen. Filter stehen in der
 * URL (?suche=&kategorie=&art=&tags=).
 */
const TemplateBrowserPage: React.FC = () => {
  const navigate = useNavigate();
  const { push } = useToast();
  const { confirm } = useConfirm();
  const { get, set, setMany } = useUrlParams();
  const suche = get('suche');
  const kategorie = get('kategorie');
  const art: Art = (['system', 'eigene'].includes(get('art')) ? get('art') : 'alle') as Art;
  const tagsParam = get('tags');
  const tags = tagsParam ? tagsParam.split(',').filter(Boolean) : [];

  const [sucheEingabe, setSucheEingabe] = useState(suche);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [kategorien, setKategorien] = useState<string[]>([]);
  const [alleTags, setAlleTags] = useState<string[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState<Template | null>(null);

  useEffect(() => {
    const id = window.setTimeout(() => {
      if (sucheEingabe.trim() !== suche) set('suche', sucheEingabe.trim(), { replace: true });
    }, 300);
    return () => window.clearTimeout(id);
  }, [sucheEingabe, suche, set]);

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      const daten = await templateApi.getTemplates({
        category: kategorie || undefined,
        search: suche || undefined,
        isSystem: art === 'system' ? true : undefined,
        myTemplates: art === 'eigene' ? true : undefined,
        tags: tagsParam ? tagsParam.split(',').filter(Boolean) : undefined,
      });
      setTemplates(daten ?? []);
      setFehler(null);
    } catch (e) {
      setFehler(fehlerText(e, 'Vorlagen konnten nicht geladen werden'));
    } finally {
      setLaedt(false);
    }
  }, [kategorie, suche, art, tagsParam]);

  useEffect(() => { laden(); }, [laden]);

  useEffect(() => {
    Promise.resolve().then(() => templateApi.getCategories()).then((k) => setKategorien(k ?? [])).catch(() => setKategorien([]));
    Promise.resolve().then(() => templateApi.getTags()).then((t) => setAlleTags(t ?? [])).catch(() => setAlleTags([]));
  }, []);

  const tagUmschalten = (tag: string) => {
    const next = tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag];
    set('tags', next.join(','), { replace: true });
  };

  const zuruecksetzen = () => {
    setSucheEingabe('');
    setMany({ suche: null, kategorie: null, art: null, tags: null }, { replace: true });
  };

  const verwenden = async (t: Template) => {
    try {
      await templateApi.useTemplate(t.id);
    } catch {
      // Zaehler ist Nebensache; der Generator soll trotzdem aufgehen.
    }
    navigate('/text-generator', { state: { template: t } });
  };

  const loeschen = async (t: Template) => {
    const ok = await confirm({ title: 'Template löschen?', message: `„${t.name}“ wird entfernt.`, confirmLabel: 'Löschen', tone: 'danger' });
    if (!ok) return;
    try {
      await templateApi.deleteTemplate(t.id);
      push({ message: `„${t.name}“ gelöscht.`, severity: 'info' });
      setOffen(null);
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Löschen fehlgeschlagen'));
    }
  };

  const gefiltert = Boolean(suche || kategorie || art !== 'alle' || tags.length);

  return (
    <div className="page-container-wide">
      <PageHeader
        eyebrow="Inhalte & Werkzeuge"
        title="Vorlagen"
        subtitle="Fertige Prompts mit Platzhaltern (Templates). „Verwenden“ öffnet den Text-Generator mit der Vorlage; eigene Vorlagen entstehen dort über „Als Vorlage speichern“."
        actions={(
          <button type="button" className="btn btn-primary" onClick={() => navigate('/text-generator')}>
            <Icon name="plus" size={14} /> Eigenes Template
          </button>
        )}
      />

      <div className="toolbar">
        <div className="toolbar__group">
          <input type="search" placeholder="Vorlagen durchsuchen…" aria-label="Vorlagen durchsuchen" value={sucheEingabe} onChange={(e) => setSucheEingabe(e.target.value)} />
          <select aria-label="Kategorie" value={kategorie} onChange={(e) => set('kategorie', e.target.value, { replace: true })}>
            <option value="">Alle Kategorien</option>
            {kategorien.map((k) => <option key={k} value={k}>{kategorieLabel(k)}</option>)}
          </select>
          <div className="segment" role="tablist" aria-label="Herkunft">
            {ARTEN.map((a) => (
              <button key={a.id} type="button" role="tab" aria-selected={art === a.id} className={`btn btn-sm ${art === a.id ? 'btn-primary' : 'btn-ghost'}`} onClick={() => set('art', a.id === 'alle' ? null : a.id, { replace: true })}>
                {a.label}
              </button>
            ))}
          </div>
        </div>
        <div className="toolbar__group toolbar__group--right">
          {gefiltert && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={zuruecksetzen}><Icon name="close" size={12} /> Filter zurücksetzen</button>
          )}
          {!laedt && <span className="text-sm text-secondary">{templates.length} Vorlagen</span>}
        </div>
        {alleTags.length > 0 && (
          <div className="tags-container toolbar__tags" aria-label="Tags">
            {alleTags.map((t) => (
              <button key={t} type="button" aria-pressed={tags.includes(t)} className={`tag tag-interactive${tags.includes(t) ? ' tag-active' : ''}`} onClick={() => tagUmschalten(t)}>
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {fehler && <div className="alert alert-error">{fehler}</div>}

      {laedt ? (
        <LoadingState rows={3} label="Vorlagen werden geladen" />
      ) : templates.length === 0 ? (
        gefiltert ? (
          <EmptyState compact icon="search" title="Kein Template passt zum Filter" action={{ label: 'Filter zurücksetzen', onClick: zuruecksetzen }} />
        ) : (
          <EmptyState
            icon="template"
            title="Noch keine Vorlagen"
            description="Schreibe im Text-Generator einen Prompt mit Platzhaltern wie {{thema}} und speichere ihn als Template – dann steht er hier."
            action={{ label: 'Zum Text-Generator', to: '/text-generator' }}
          />
        )
      ) : (
        <div className="grid-cards-3">
          {templates.map((t) => (
            <article key={t.id} className="card template-karte" aria-label={t.name}>
              <div className="template-karte__kopf">
                <h3 className="card-title">
                  <button type="button" className="link-button" onClick={() => setOffen(t)}>{t.name}</button>
                </h3>
                {t.isSystem ? <StatusBadge tone="info">Vorlage</StatusBadge> : <StatusBadge tone="neutral">Eigenes</StatusBadge>}
              </div>
              <p className="card-description">{t.description || 'Ohne Beschreibung'}</p>
              <div className="text-xs text-secondary">
                {kategorieLabel(t.category)} · {t.variables.length} {t.variables.length === 1 ? 'Platzhalter' : 'Platzhalter'} · {t.usageCount}× verwendet
              </div>
              {t.tags.length > 0 && (
                <div className="tags-container">{t.tags.slice(0, 4).map((tag) => <span key={tag} className="tag">{tag}</span>)}</div>
              )}
              <div className="card-footer">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOffen(t)}><Icon name="eye" size={14} /> Ansehen</button>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => verwenden(t)}>Verwenden <Icon name="arrow-right" size={14} /></button>
              </div>
            </article>
          ))}
        </div>
      )}

      <Modal open={Boolean(offen)} onOpenChange={(o) => { if (!o) setOffen(null); }}>
        {offen && (
          <ModalContent className="detail-modal">
            <ModalHeader>
              <ModalTitle>{offen.name}</ModalTitle>
              <ModalDescription>{kategorieLabel(offen.category)}{offen.description ? ` · ${offen.description}` : ''}</ModalDescription>
            </ModalHeader>
            <ModalBody>
              {offen.variables.length > 0 && (
                <>
                  <h3>Platzhalter</h3>
                  <dl className="sales-facts">
                    {offen.variables.map((v) => (
                      <div key={v.name}>
                        <dt><code>{`{{${v.name}}}`}</code></dt>
                        <dd>{v.description || '—'}{v.example && <span className="text-xs text-secondary"> · z. B. {v.example}</span>}</dd>
                      </div>
                    ))}
                  </dl>
                </>
              )}
              <h3>Prompt</h3>
              <pre className="code-block">{offen.prompt}</pre>
              {offen.systemPrompt && (
                <>
                  <h3>System-Prompt</h3>
                  <pre className="code-block">{offen.systemPrompt}</pre>
                </>
              )}
              {offen.tags.length > 0 && (
                <div className="tags-container">{offen.tags.map((tag) => <span key={tag} className="tag">{tag}</span>)}</div>
              )}
            </ModalBody>
            <ModalFooter>
              {!offen.isSystem && offen.userId && (
                <button type="button" className="btn btn-ghost" onClick={() => loeschen(offen)}><Icon name="trash" size={14} /> Löschen</button>
              )}
              <button type="button" className="btn btn-primary" onClick={() => verwenden(offen)}>Im Text-Generator verwenden</button>
            </ModalFooter>
          </ModalContent>
        )}
      </Modal>
    </div>
  );
};

export default TemplateBrowserPage;
